import { useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateGeographicBoundary,
  useUpdateGeographicBoundary,
  createGeographicBoundarySchema,
  type CreateGeographicBoundaryFormData,
} from "@/api/geographic-boundaries";
import type { GeographicBoundary } from "@/api.types.v2";
import { toast } from "sonner";
import { useCodeSuggestion } from "@/hooks/use-code-suggestion";

interface CreateGeographicBoundaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues?: Partial<GeographicBoundary>;
  mode?: "create" | "edit";
}

export function CreateGeographicBoundaryDialog({
  open,
  onOpenChange,
  defaultValues,
  mode = "create",
}: CreateGeographicBoundaryDialogProps) {
  const userEditedCode = useRef(false);

  const form = useForm<CreateGeographicBoundaryFormData>({
    resolver: zodResolver(createGeographicBoundarySchema),
    defaultValues: {
      name: defaultValues?.name ?? "",
      code: defaultValues?.code ?? "",
      source: defaultValues?.source ?? "manual",
      boundary:
        (defaultValues?.boundary as CreateGeographicBoundaryFormData["boundary"]) ?? {
          type: "Polygon",
          coordinates: [[[0, 0]]],
        },
      metadata: defaultValues?.metadata ?? {},
      isActive: defaultValues?.isActive ?? true,
    },
  });

  // Code suggestion hook - only in create mode
  const { handleNameChange: suggestCodeFromName } = useCodeSuggestion({
    onCodeSuggested: (code) => {
      if (!userEditedCode.current) {
        form.setValue("code", code);
      }
    },
    enabled: mode === "create",
  });

  const createMutation = useCreateGeographicBoundary({
    onSuccess: () => {
      toast.success("Geographic boundary created successfully");
      form.reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create geographic boundary");
    },
  });

  const updateMutation = useUpdateGeographicBoundary({
    onSuccess: () => {
      toast.success("Geographic boundary updated successfully");
      form.reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update geographic boundary");
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    if (mode === "edit" && defaultValues?.id) {
      updateMutation.mutate({ ...values, id: defaultValues.id });
    } else {
      createMutation.mutate(values);
    }
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Create Geographic Boundary"
              : "Edit Geographic Boundary"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Richmond Hill"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          suggestCodeFromName(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="richmond_hill"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          field.onChange(e);
                          userEditedCode.current = true;
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="osm">OpenStreetMap</SelectItem>
                        <SelectItem value="geojson">GeoJSON</SelectItem>
                        <SelectItem value="overpass">Overpass Turbo</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                    <div className="flex-1">
                      <FormLabel>Active</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={Boolean(field.value)}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="boundary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Boundary (GeoJSON Polygon)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"type": "Polygon", "coordinates": [[[lng, lat], [lng, lat], ...]]}'
                      className="h-32 font-mono text-sm"
                      value={JSON.stringify(field.value, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(
                            e.target.value
                          ) as CreateGeographicBoundaryFormData["boundary"];
                          field.onChange(parsed);
                        } catch {
                          // Keep the current value if JSON is invalid
                        }
                      }}
                    />
                  </FormControl>
                  <p className="text-xs text-gray-500">
                    Enter a valid GeoJSON Polygon object with coordinates
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="metadata"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Metadata (Optional JSON)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"population": 195000, "area": 101}'
                      className="h-24 font-mono text-sm"
                      value={JSON.stringify(field.value ?? {}, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value) as Record<
                            string,
                            unknown
                          >;
                          field.onChange(parsed);
                        } catch {
                          // Keep the current value if JSON is invalid
                        }
                      }}
                    />
                  </FormControl>
                  <p className="text-xs text-gray-500">
                    Additional metadata in JSON format (e.g., population, admin
                    level, etc.)
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? mode === "create"
                    ? "Creating..."
                    : "Updating..."
                  : mode === "create"
                    ? "Create Boundary"
                    : "Update Boundary"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
